<template>
  <div>
    <navigation />
    <div class="page-body">
      <div class="experience-switch" v-if="!bannerDismissed">
        <div class="experience-msg">
          <span class="experience-pill">New</span>
          <span class="experience-text">You're using the new create proof.</span>
        </div>
        <div class="experience-actions">
          <button
            type="button"
            class="experience-link experience-link-btn"
            v-b-modal="'feedbackModal-newCreateProof'"
          >Give feedback</button>
          <span class="experience-divider" aria-hidden="true">·</span>
          <router-link to="/create" class="experience-link">Switch to classic →</router-link>
        </div>
        <button
          type="button"
          class="experience-close"
          @click="dismissBanner"
          aria-label="Dismiss banner"
          title="Dismiss"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      <feedbackModal
        name="feedbackModal-newCreateProof"
        campaign-key="new-create-proof-v1"
        title="How's the new create proof page?"
        subtitle="Your take helps us decide what to keep."
        scale-question="Compared to the classic experience, how would you rate this?"
        :scale-min="1"
        :scale-max="7"
        scale-low-label="Much worse"
        scale-high-label="Much better"
        text-prompt="What's working or not working?"
        text-placeholder="Tell us anything that helps."
        submit-label="Send feedback"
      />

      <div class="composer-canvas">
        <div class="composer">

          <!-- ====== HERO / NAME ====== -->
          <header class="composer-hero">
            <span class="hero-eyebrow">New proof</span>
            <div class="hero-name-wrap">
              <input
                id="proof_name"
                ref="proofNameInput"
                type="text"
                class="hero-name-input"
                placeholder="Untitled proof"
                autocomplete="off"
                v-model="proof.name"
              />
              <span class="hero-name-rule" aria-hidden="true"></span>
            </div>
            <p class="hero-hint">Name it, or we'll borrow the name of your first file.</p>
          </header>

          <div class="composer-stack">

            <!-- ====== STEP 01: FILES ====== -->
            <section class="composer-card files-card">
              <div class="card-step">
                <span class="step-num">01</span>
                <div class="step-text">
                  <h2 class="step-title">Add your files</h2>
                  <p class="step-sub">Upload documents, images, audio or video — or capture a live website.</p>
                </div>
              </div>
              <proofFilepicker
                id="proof_filepicker"
                ref="filePicker"
                :existingFile="existingFile"
                @fileAdded="onFileAdded"
              />
            </section>

            <!-- ====== STEP 02: REVIEW SETUP ====== -->
            <section class="composer-card rail-card">
                <div class="card-step">
                  <span class="step-num">02</span>
                  <div class="step-text">
                    <h2 class="step-title">Set up the review</h2>
                    <p class="step-sub">Permissions and properties for this proof.</p>
                  </div>
                </div>

                <div class="rail-block" id="proof_options">
                  <span class="rail-label">Approver permissions</span>
                  <div class="permissions-list">
                    <div
                      v-for="perm in permissionDefs"
                      :key="perm.key"
                      class="perm-row ashore-toggle"
                      :class="{ 'perm-locked': perm.locked }"
                      :id="'proof_toggle_' + perm.key"
                      @click="perm.locked ? $A.BuyNow() : null"
                    >
                      <label class="switch mb-0 opacity-100">
                        <input
                          type="checkbox"
                          v-model="settings[perm.key]"
                          :disabled="perm.locked"
                        />
                        <div class="slider round"></div>
                      </label>
                      <div class="perm-text">
                        <span class="perm-title">
                          {{ perm.label }}
                          <span v-if="perm.locked" class="perm-upgrade-badge">Upgrade</span>
                        </span>
                        <span class="perm-desc">{{ perm.description }}</span>
                      </div>
                    </div>
                  </div>
                  <div v-if="settings.passwordEnabled" class="mt-2" id="proof_password_block">
                    <span class="rail-label">Proof password</span>
                    <input
                      type="password"
                      class="rail-text-input"
                      v-model="proofPassword"
                      placeholder="Enter a password"
                      autocomplete="new-password"
                    />
                  </div>
                </div>

                <div v-if="teams.length > 0" class="rail-block">
                  <span class="rail-label">Team</span>
                  <multiselect
                    v-model="usersTeamIds"
                    :options="teams"
                    :placeholder="optionsPlaceholder"
                    :multiple="true"
                    :show-labels="false"
                    :hide-selected="true"
                    :clear-on-select="true"
                    :searchable="true"
                    label="name"
                    track-by="id"
                    class="rail-multiselect"
                  />
                </div>

                <div class="rail-block">
                  <span class="rail-label">Deadline</span>
                  <datepicker
                    placeholder="MM-DD-YYYY"
                    v-model="dueDate"
                    data-cy="datepickerInput"
                  />
                </div>
                <div class="rail-block">
                  <span class="rail-label">Tags</span>
                  <tags ref="tags" />
                </div>
                <div v-for="property in customProperties" :key="property.id" class="rail-block">
                  <div v-if="property.dataType === 'Datepicker'">
                    <span class="rail-label">{{ property.label }}</span>
                    <datepicker
                      v-model="customPropertyValues[property.id]"
                      class="d-block"
                      placeholder="MM-DD-YYYY"
                    />
                  </div>
                  <div v-if="property.dataType === 'Single Line Text'">
                    <span class="rail-label">{{ property.label }}</span>
                    <input type="text" class="rail-text-input" v-model="customPropertyValues[property.id]" />
                  </div>
                  <div v-if="property.dataType === 'Dropdown Select'">
                    <span class="rail-label">{{ property.label }}</span>
                    <v-select
                      :options="property.enum"
                      :value="customPropertyValues[property.id]"
                      placeholder="Select"
                      class="rail-select"
                      @input="(value) => customPropertyValues[property.id] = value"
                    />
                  </div>
                  <div class="rail-checkboxes" v-if="property.dataType === 'Checkboxes (Multiple Options)'">
                    <span class="rail-label">{{ property.label }}</span>
                    <b-form-checkbox-group
                      v-model="customPropertyValues[property.id]"
                      :options="property.enum"
                      inline
                      class="d-flex flex-column"
                    ></b-form-checkbox-group>
                  </div>
                </div>
            </section>

            <!-- ====== STEP 03: CREATE ====== -->
            <section class="composer-card deliver-card">
                <div class="card-step">
                  <span class="step-num">03</span>
                  <div class="step-text">
                    <h2 class="step-title">Create</h2>
                    <p class="step-sub">We'll upload your files and generate a review link you can share.</p>
                  </div>
                </div>
                <a
                  v-show="canUserSendFile && !submitted"
                  id="proof_link"
                  class="deliver-btn"
                  @click="createProof()"
                >
                  Create Proof
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </a>
                <a
                  v-show="canUserSendFile && submitted && erroredOut"
                  class="deliver-btn"
                  @click="createProof()"
                >
                  Continue Uploading
                </a>
                <button
                  v-show="!canUserSendFile"
                  class="button button-locked deliver-locked"
                  @click="$A.BuyNow()"
                  v-b-tooltip.hover="'Upgrade To Send More Proofs'"
                >
                  Feature Locked <img src="/img/feature-lock.svg" />
                </button>
            </section>
          </div>
        </div>
      </div>
    </div>

    <proofCreated :proof="proof" />
    <newUserWelcomeModal name="NewUserWelcomeModal" />
    <onboardingModelState v-if="showOnboardingStepModal" :todos="pendingTodoItems" :current-onboarding-step="currentOnboardingStep" :is-proof-created="true" />
  </div>
</template>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<script>
import proofFilepicker from "../components/create/proof-filepicker.vue";
import Datepicker from "vuejs-datepicker";
import tags from "../components/form/tags.vue";
import proofCreated from "../components/modals/modal-proof-created-v2.vue";
import newUserWelcomeModal from "../components/modals/modal-new-user-welcome.vue";
import Multiselect from "vue-multiselect";
import onboardingModelState from "../components/modals/modal-onboardingTodos.vue";
import { getPendingTodoItems, isWatermarkPlanEnabled, isProofPasswordPlanEnabled } from "../utils/helpers";
import { ONBOARDING_TODO_ITEMS, MODAL_NAMES } from "../utils/constants";

export default {
  components: {
    proofFilepicker,
    Datepicker,
    tags,
    proofCreated,
    newUserWelcomeModal,
    Multiselect,
    onboardingModelState,
  },
  data() {
    let dismissed = false;
    try {
      dismissed = window.localStorage.getItem("experience-banner::create-proof::dismissed") === "1";
    } catch (_e) { /* ignore */ }
    const userSettings = _.get(this, "$A.Auth.userSettings", {}) || {};
    return {
      settings: {
        downloadFiles: !!userSettings.downloadFiles,
        approveIndividualFiles: !!userSettings.approveIndividualFiles,
        viewAllComments: !!userSettings.viewAllComments,
        approveWithChanges: !!userSettings.approveWithChanges,
        limitVersionVisibility: !!userSettings.limitVersionVisibility,
        blockCommentingAfterProofDecision: !!userSettings.blockCommentingAfterProofDecision,
        watermarkEnabled: isWatermarkPlanEnabled(this.$A.Auth) && !!userSettings.watermarkEnabled,
        passwordEnabled: false,
      },
      proofPassword: "",
      permissionDefs: [
        {
          key: "downloadFiles",
          label: "Allow file downloads",
          description: "Approvers can download the original files in this proof.",
        },
        {
          key: "approveIndividualFiles",
          label: "Approve each file separately",
          description: "Ask for a decision on every file, instead of one decision for the whole proof.",
        },
        {
          key: "viewAllComments",
          label: "Share comments between reviewers",
          description: "Approvers and commenters can see, reply to, and get notified about each other's comments.",
        },
        {
          key: "approveWithChanges",
          label: "Allow “Approve with changes”",
          description: "Adds a third decision option — approve while requesting changes, without reviewing a new version.",
        },
        {
          key: "limitVersionVisibility",
          label: "Limit version visibility",
          description: "Approvers can't see versions that were sent to other workflow stages.",
        },
        {
          key: "blockCommentingAfterProofDecision",
          label: "Lock comments after a decision",
          description: "Once an approver submits their decision, they can no longer add comments.",
        },
        {
          key: "watermarkEnabled",
          label: "Watermark files",
          description: "Stamps your account logo on every page of PDFs uploaded to this proof.",
          locked: !isWatermarkPlanEnabled(this.$A.Auth),
        },
        {
          key: "passwordEnabled",
          label: "Password protect this proof",
          description: "Approvers must enter a password before they can open this proof.",
          locked: !isProofPasswordPlanEnabled(this.$A.Auth),
        },
      ],
      bannerDismissed: dismissed,
      user: this.$A.Auth,
      proofId: 0,
      proofVersionId: 0,
      submitted: false,
      erroredOut: false,
      onboardingState: null,
      pendingTodoItems: [],
      currentOnboardingStep: ONBOARDING_TODO_ITEMS.CREATE_PROOF,
      showOnboardingStepModal: false,
      proof: {
        name: "",
        dueDate: "",
        password: "",
        allowDownload: false,
        allowEdit: false,
        allowApproveWithChanges: false,
        allowViewAllComments: false,
        tagIds: [],
      },
      dueDate: "",
      customProperties: [],
      customPropertyValues: {},
      teams: [],
      usersTeamIds: [],
      optionsPlaceholder: "Search and Select ...",
    };
  },
  async mounted() {
    this.$bvModal.show(MODAL_NAMES.ONBOARDING_MODEL);
    if (this.$route.query.welcome !== undefined) {
      this.$nextTick(() => {
        this.$bvModal.show("NewUserWelcomeModal");
      });
    }
    this.$A.ClearViewData();
    await this.$A.GetCurAccountData();
    await this.loadTeams();
    await this.loadCustomProperties();
  },
  beforeDestroy() {
    // Don't let an abandoned submission hand-off mark a later, unrelated
    // proof as the submission's conversion target.
    if (window.$A) {
      window.$A.proofSubmissionIdToLink = null;
      window.$A.proofCustomFieldsToPrefill = null;
    }
  },
  computed: {
    canUserSendFile: function () {
      return _.get(this.user, "account.subscriptionState.canSendANewFile", false);
    },
    existingFile: function () {
      const pdfFile = window.$A?.preflightPdf;
      if (pdfFile) {
        const { name, size } = pdfFile;
        return [{
          uuid: 1,
          id: 0,
          file: pdfFile,
          name,
          mb: Math.ceil(size / (1024 * 1024)),
          audio: false,
          web: false,
          video: false,
          image: false,
          document: true,
          unsupported: false,
          uploadProgress: 0,
          error: "",
          skip: false,
        }];
      }

      const filesForProof = window.$A?.filesForProof;
      if (Array.isArray(filesForProof) && filesForProof.length) {
        return filesForProof.map((f, i) => {
          // Two shapes supported:
          //   { file: File }                  -> existing local-File path (e.g. Preflight)
          //   { urlSource, name, sizeBytes }  -> server-side fetch path (used by /files)
          const isUrlSource = !!f.urlSource && !f.file;
          const type = (isUrlSource ? f.mimeType : f.type || "").toLowerCase();
          const sizeBytes = isUrlSource ? (f.sizeBytes || 0) : (f.size || 0);
          return {
            uuid: i + 1,
            id: 0,
            file: isUrlSource ? null : f,
            urlSource: isUrlSource ? f.urlSource : undefined,
            name: f.name,
            mb: Math.ceil(sizeBytes / (1024 * 1024)),
            audio: type.startsWith("audio/"),
            video: type.startsWith("video/"),
            image: type.startsWith("image/"),
            web: type === "text/html",
            document: !type.startsWith("audio/") && !type.startsWith("video/") && !type.startsWith("image/") && type !== "text/html",
            unsupported: false,
            uploadProgress: 0,
            error: "",
            skip: false,
          };
        });
      }

      return [];
    },
  },
  methods: {
    dismissBanner() {
      this.bannerDismissed = true;
      try {
        window.localStorage.setItem("experience-banner::create-proof::dismissed", "1");
      } catch (_e) { /* ignore */ }
    },
    getCurrentCustomProperties: function () {
      return this.customProperties.map((prop) => {
        prop.value = this.customPropertyValues[prop.id];
        if (prop.value) {
          if (prop.dataType === "Datepicker") {
            prop.value = [new Date(prop.value).toJSON()];
          } else if (prop.dataType === "Single Line Text" || prop.dataType === "Dropdown Select") {
            prop.value = [prop.value];
          }
        } else {
          prop.value = [];
        }
        return {
          accountId: prop.accountId,
          proofPropertyValue: prop.value,
          accountProofPropertyId: prop.id,
          accountProofPropertyEnum: prop.enum,
          accountProofPropertyLabel: prop.label,
          accountProofPropertyDataType: prop.dataType,
        };
      });
    },
    loadCustomProperties: async function () {
      this.customProperties = await this.$A.AccountService.GetAllCustomProofProperties();
      _.isArray(this.customProperties) && this.customProperties.forEach((prop) => {
        this.customPropertyValues[prop.id] = null;
        if (prop.dataType === "Checkboxes (Multiple Options)") {
          this.customPropertyValues[prop.id] = [];
        }
      });
      this.applyCustomFieldPrefill();
    },
    applyCustomFieldPrefill: function () {
      // Set by the artwork-intake submission "Create Proof" quick action.
      const prefill = window.$A?.proofCustomFieldsToPrefill;
      if (!Array.isArray(prefill) || !prefill.length || !_.isArray(this.customProperties)) return;
      prefill.forEach((field) => {
        const prop = this.customProperties.find((p) => p.id === field.accountProofPropertyId);
        if (!prop) return;
        const values = (Array.isArray(field.value) ? field.value : [])
          .filter((v) => v !== "" && v != null);
        if (!values.length) return;
        this.customPropertyValues[prop.id] =
          prop.dataType === "Checkboxes (Multiple Options)" ? values : values[0];
      });
      window.$A.proofCustomFieldsToPrefill = null;
    },
    createProof: async function () {
      if (this.proof.name.length === 0) {
        this.$A.AlertUser(
          "Please Enter A Proof Name Before Continuing",
          "warning",
          10000
        );
        this.$refs.proofNameInput && this.$refs.proofNameInput.focus();
        return;
      }

      const passwordProtectionOn =
        isProofPasswordPlanEnabled(this.$A.Auth) && this.settings.passwordEnabled;
      if (passwordProtectionOn && this.proofPassword.length === 0) {
        this.$A.AlertUser(
          "Please Enter A Proof Password Or Turn Off Password Protection",
          "warning",
          10000
        );
        return;
      }

      try {
        let proofSettings = this.settings;
        this.proof.allowDownload = proofSettings.downloadFiles;
        this.proof.allowApproveIndividualFiles = proofSettings.approveIndividualFiles;
        this.proof.allowApproveWithChanges = proofSettings.approveWithChanges;
        this.proof.allowViewAllComments = proofSettings.viewAllComments;
        this.proof.limitVersionVisibility = proofSettings.limitVersionVisibility;
        this.proof.blockCommentingAfterProofDecision = proofSettings.blockCommentingAfterProofDecision;
        this.proof.watermarkEnabled = isWatermarkPlanEnabled(this.$A.Auth) && !!proofSettings.watermarkEnabled;
        this.proof.password = passwordProtectionOn ? this.proofPassword : "";
        this.proof.dueDate = new Date(this.dueDate).toJSON();
        this.proof.tagIds = this.$refs.tags.getSelectedTagIds();
        this.proof.customFields = this.getCurrentCustomProperties();
        this.proof.usersTeamIds = this.usersTeamIds.map((t) => t.id);
      } catch (e) {
        console.log(e);
        return;
      }

      this.submitted = true;

      if (this.proof.id === undefined) {
        let proof = await this.$A.ProofService.CreateUpdateProof(
          this.proofId,
          this.proof
        );
        if (proof && proof.id > 0) {
          this.proofId = proof.id;
          this.proofVersionId = proof.currentVersion.id;
          this.proof = proof;
          this.proof.password = "";
          this.proofPassword = "";

          const jobIdToLink = window.$A?.proofJobIdToLink;
          if (jobIdToLink) {
            try {
              await this.$A.JobService.updateAndDeleteProofJobLink({
                jobId: jobIdToLink,
                update: [proof.id],
                delete: [],
              });
            } catch (linkErr) {
              console.error("Failed to link proof to job:", linkErr);
            }
            window.$A.proofJobIdToLink = null;
          }

          const submissionIdToLink = window.$A?.proofSubmissionIdToLink;
          if (submissionIdToLink) {
            try {
              await this.$A.ArtworkIntakeService.convertSubmissionToProof(
                submissionIdToLink,
                proof.id
              );
            } catch (convertErr) {
              console.error("Failed to mark submission as converted to proof:", convertErr);
            }
            window.$A.proofSubmissionIdToLink = null;
          }
        } else {
          this.submitted = false;
          return;
        }
      }

      this.$A.AlertUser("Uploading Files. Please Wait...", "success", 100000);

      await this.$refs.filePicker.UploadFiles(
        this.proofId,
        this.proof.currentVersion.id
      );
      if (
        this.$refs.filePicker
          .GetFileStatus()
          .filter((f) => f.uploadProgress === 100).length !==
        this.$refs.filePicker.GetFileStatus().length
      ) {
        this.$A.AlertUser(
          "There Was A Problem Uploading Your Files",
          "warning",
          10000
        );
        this.erroredOut = true;
        return;
      }
      if (
        this.$refs.filePicker
          .GetFileStatus()
          .filter((f) => f.noFilesLeft !== undefined).length > 0
      ) {
        this.$A.AlertUser(
          "You Have Run Out Of Files, Upgrade Now To Unlock More",
          "warning",
          10000
        );
        return;
      }

      const orderedFiles = this.$refs.filePicker.GetFileIDsOrdered();
      await this.$A.ProofFileService.UpdateOrderForProofVersionFiles(this.proofId, this.proof.currentVersion.id, orderedFiles);

      this.$A.AlertUser("All Files Uploaded", "success", 10000);

      try {
        await this.$A.UserService.updateOnboardingState({
          todoState: {
            hasCreatedProof: true,
          },
        });
      } catch (error) {
        console.error("Failed to update onboarding state:", error);
      }

      try {
        this.onboardingState = await this.$A.UserService.getOnboardingState();
        const { todoState, modelState } = this.onboardingState;

        const shouldShowProofCreatedModal =
          todoState.hasCreatedProof && !modelState.hasCreatedProofModelShown;

        if (shouldShowProofCreatedModal) {
          this.pendingTodoItems = getPendingTodoItems(this.onboardingState);
          this.showOnboardingStepModal = true;
          try {
            await this.$A.UserService.updateOnboardingState({
              modelState: { hasCreatedProofModelShown: true },
            });
          } catch (updateError) {
            console.error("Failed to update onboarding model state:", updateError);
            this.$bvModal.show(MODAL_NAMES.PROOF_CREATED);
          }
        } else {
          this.$bvModal.show(MODAL_NAMES.PROOF_CREATED);
        }
      } catch (loadError) {
        console.error("Failed to load onboarding modal:", loadError);
        this.$bvModal.show(MODAL_NAMES.PROOF_CREATED);
      }
    },
    onFileAdded: function (fileRecord) {
      // Auto-fill proof name with filename if proof name is empty
      if (!this.proof.name || this.proof.name.trim() === "") {
        let fileName = fileRecord.name;

        // If it's a URL, use the domain name
        if (fileRecord.web === 1) {
          try {
            const url = new URL(fileName);
            fileName = url.hostname;
          } catch (e) {
            // If URL parsing fails, use the full URL but truncate if too long
            fileName = fileName.length > 50 ? fileName.substring(0, 50) + "..." : fileName;
          }
        } else {
          // For regular files, remove the file extension
          const lastDotIndex = fileName.lastIndexOf(".");
          if (lastDotIndex > 0) {
            fileName = fileName.substring(0, lastDotIndex);
          }
        }

        this.proof.name = fileName;
      }
    },
    loadTeams: async function () {
      this.teams = await this.$A.TeamService.GetAccountTeams();
      const userTeams = await this.$A.TeamService.GetTeams();
      const team = userTeams.find((t) => t.users.find((u) => u.userId === this.user.id && u.defaultTeamForUser));
      if (team) this.usersTeamIds = [{ ...team }];
    },
  },
};
</script>

<style scoped>
/* ============== SHELL ============== */
.page-body {
  margin-top: 64px; /* clear position:fixed nav on desktop */
  min-height: calc(100vh - 64px);
  display: flex;
  flex-direction: column;
}
@media (max-width: 991px) {
  .page-body {
    margin-top: 0;
    min-height: 100vh;
  }
}

.composer-canvas {
  flex: 1;
  background:
    radial-gradient(ellipse 900px 420px at 50% -120px, rgba(1, 146, 86, 0.05), transparent),
    #f6f7f9;
  padding: 40px 24px 96px;
}

.composer {
  max-width: 840px;
  margin: 0 auto;
}

/* ============== HERO ============== */
.composer-hero {
  padding: 8px 4px 32px;
  animation: composer-rise 480ms cubic-bezier(0.22, 1, 0.36, 1) backwards;
}

.hero-eyebrow {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #019256;
  margin-bottom: 10px;
}

.hero-name-wrap {
  position: relative;
  max-width: 720px;
}

.hero-name-input {
  display: block;
  width: 100%;
  border: 0;
  background: transparent;
  padding: 0 0 10px;
  font-size: 34px;
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1.2;
  color: #111827;
  outline: none;
  border-bottom: 1px solid rgba(15, 23, 42, 0.14);
  border-radius: 0;
  transition: border-color 160ms ease;
}
.hero-name-input::placeholder {
  color: #c4c9d2;
  font-weight: 600;
}

.hero-name-rule {
  position: absolute;
  left: 0;
  bottom: 0;
  height: 2px;
  width: 100%;
  background: #019256;
  transform: scaleX(0);
  transform-origin: left center;
  transition: transform 280ms cubic-bezier(0.22, 1, 0.36, 1);
}
.hero-name-input:focus + .hero-name-rule {
  transform: scaleX(1);
}

.hero-hint {
  margin: 10px 0 0;
  font-size: 13px;
  color: #6b7280;
}

/* ============== STACK ============== */
.composer-stack {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

@media (max-width: 991px) {
  .hero-name-input {
    font-size: 26px;
  }
}
@media (max-width: 700px) {
  .composer-canvas {
    padding: 24px 12px 64px;
  }
}

/* ============== CARDS ============== */
/* No overflow:hidden here — it clipped the datepicker popup on the last
   fields of a card. Rounded corners are handled on .card-step instead. */
.composer-card {
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.07);
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(8, 11, 13, 0.04), 0 8px 24px -16px rgba(8, 11, 13, 0.10);
  animation: composer-rise 480ms cubic-bezier(0.22, 1, 0.36, 1) backwards;
}
.files-card { animation-delay: 60ms; }
.rail-card { animation-delay: 120ms; }
.deliver-card { animation-delay: 180ms; }

/* End at transform:none (not translateY(0)) — a retained transform creates a
   stacking context on every card, trapping datepicker/select popups beneath
   later sibling cards. */
@keyframes composer-rise {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

.card-step {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 20px 24px 16px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  background: #fbfbfc;
  border-radius: 11px 11px 0 0;
}

.step-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  border-radius: 999px;
  background: rgba(1, 146, 86, 0.10);
  color: #019256;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.step-title {
  margin: 3px 0 2px;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: #111827;
}

.step-sub {
  margin: 0;
  font-size: 12.5px;
  line-height: 1.45;
  color: #6b7280;
}

/* ============== RAIL DETAILS ============== */
.rail-block {
  padding: 14px 24px 0;
}
.rail-card > .rail-block:last-of-type {
  padding-bottom: 14px;
}

.rail-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6b7280;
  margin-bottom: 6px;
}

.rail-text-input {
  display: block;
  width: 100%;
  padding: 9px 13px;
  height: 38px;
  border: 1px solid #d1d5db;
  box-shadow: 0 1px 2px rgba(8, 11, 13, 0.05);
  border-radius: 6px;
  font-size: 14px;
  color: #1f2937;
  outline: none;
  transition: border-color 120ms ease, box-shadow 120ms ease;
}
.rail-text-input:focus {
  border-color: #019256;
  box-shadow: 0 0 0 3px rgba(1, 146, 86, 0.12);
}

.rail-select ::v-deep .vs__dropdown-toggle {
  padding: 0;
  width: 100%;
  height: 38px;
  border: 1px solid #d1d5db;
  box-shadow: 0 1px 2px rgba(8, 11, 13, 0.05);
  border-radius: 6px;
  font-size: 14px;
  color: #1f2937;
}

.rail-checkboxes {
  font-weight: 400;
}
.rail-checkboxes ::v-deep .custom-control-input:checked ~ .custom-control-label::after {
  background-color: #019256 !important;
  border-radius: 0.25rem;
}

.rail-multiselect ::v-deep .multiselect__tags {
  border: 1px solid #d1d5db;
  border-radius: 6px;
}
.rail-multiselect ::v-deep .multiselect__tag {
  background: #d1f6d4 !important;
  border-radius: 10px;
  font-size: 12px;
  color: #3e3e40 !important;
}

.permissions-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 6px;
}

.perm-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.perm-row .switch {
  flex-shrink: 0;
  margin-top: 1px;
}

.perm-text {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}
.perm-title {
  font-size: 13.5px;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.4;
}
.perm-desc {
  font-size: 12.5px;
  line-height: 1.5;
  color: #6b7280;
}
.perm-locked {
  cursor: pointer;
}
.perm-locked .switch {
  opacity: 0.4;
  cursor: not-allowed;
}
.perm-locked .switch .slider {
  background-color: #d1d5db !important;
}
.perm-locked .perm-title {
  color: #9ca3af;
}
.perm-locked .perm-desc {
  opacity: 0.6;
}
.perm-upgrade-badge {
  display: inline-flex;
  align-items: center;
  margin-left: 6px;
  padding: 1px 8px;
  background: #019256;
  color: #fff;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  line-height: 18px;
  vertical-align: 1px;
  cursor: pointer;
  transition: background-color 120ms ease;
}
.perm-locked:hover .perm-upgrade-badge {
  background: #017a48;
}

/* ============== DELIVER ============== */
.deliver-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: fit-content;
  min-width: 220px;
  margin: 20px 24px;
  margin-left: auto;
  padding: 12px 24px;
  background: #019256;
  color: #f9f9f9;
  font-size: 14px;
  font-weight: 600;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(1, 146, 86, 0.25), 0 8px 16px -8px rgba(1, 146, 86, 0.45);
  cursor: pointer;
  text-decoration: none;
  transition: background-color 140ms ease, transform 80ms ease;
}
.deliver-btn:hover {
  background: #017a48;
  color: #fff;
  text-decoration: none;
}
.deliver-btn:active {
  transform: translateY(1px);
}

.deliver-locked {
  margin: 20px 24px;
  min-width: 0;
  width: calc(100% - 48px);
}

@media (max-width: 700px) {
  .deliver-btn {
    width: calc(100% - 48px);
  }
}

/* ============== EXPERIENCE BANNER ============== */
.experience-switch {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  column-gap: 16px;
  row-gap: 4px;
  padding: 8px 48px;
  background: rgba(1, 146, 86, 0.06);
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  font-size: 13px;
  color: #4b5563;
  flex-shrink: 0;
}
.experience-msg,
.experience-actions {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.experience-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  background: #019256;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  border-radius: 999px;
  letter-spacing: 0.02em;
}
.experience-text { color: #4b5563; }
.experience-link {
  color: #019256;
  font-weight: 600;
  text-decoration: none;
}
.experience-link:hover { text-decoration: underline; color: #017a48; }
.experience-link-btn {
  background: transparent;
  border: 0;
  padding: 0;
  font: inherit;
  cursor: pointer;
}
.experience-divider {
  color: #c2c8d0;
  font-weight: 600;
}
.experience-close {
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: transparent;
  border: 0;
  border-radius: 6px;
  color: #6b7280;
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease;
}
.experience-close:hover {
  background: rgba(15, 23, 42, 0.06);
  color: #111827;
}
</style>
